
import { useState, useEffect } from 'react';
import { Node, Edge } from '@xyflow/react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface NodeData extends Record<string, unknown> {
  name: string;
  componentTypeId: string;
  title?: string;
  uuid?: string;
  selectedComponentId?: string;
  selectedComponentName?: string;
  componentTypeName?: string;
  componentTypeIcon?: string;
  codeChanges7Days?: number;
  codeChanges30Days?: number;
  deploys7Days?: number;
}

export function useDiagramLoad(
  id: string | undefined, 
  setNodes: React.Dispatch<React.SetStateAction<Node<NodeData>[]>>,
  setEdges: React.Dispatch<React.SetStateAction<Edge[]>>,
  setHistory: React.Dispatch<React.SetStateAction<Array<{nodes: Node<NodeData>[]; edges: Edge[]}>>>,
  setHistoryIndex: React.Dispatch<React.SetStateAction<number>>
) {
  const [diagramName, setDiagramName] = useState<string>("");
  const [diagramLoaded, setDiagramLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Load diagram information
  useEffect(() => {
    const fetchDiagramInfo = async () => {
      console.log('🔍 [useDiagramLoad] Starting to fetch diagram info for ID:', id);
      
      // Evitar múltiplas execuções simultâneas
      if (isLoading || diagramLoaded) {
        console.log('⏸️ [useDiagramLoad] Skipping load - already loading or loaded');
        return;
      }
      
      if (id) {
        setIsLoading(true);
        setDiagramLoaded(false);
        try {
          const { data, error } = await supabase
            .from("diagrams")
            .select("name, structure")
            .eq("id", id)
            .single();
          
          if (error) {
            console.error("Error fetching diagram info:", error);
            return;
          }
          
          if (data) {
            console.log('📋 [useDiagramLoad] Diagram data found:', { name: data.name, hasStructure: !!data.structure });
            setDiagramName(data.name);
            
            // If we have diagram structure, parse and load it
            if (data.structure) {
              try {
                // Make sure we're parsing a string to avoid TypeScript errors
                const structureStr = typeof data.structure === 'string' 
                  ? data.structure 
                  : JSON.stringify(data.structure);
                  
                const diagramData = JSON.parse(structureStr);
                console.log('🏗️ [useDiagramLoad] Parsed diagram data:', { 
                  nodeCount: diagramData.nodes?.length, 
                  edgeCount: diagramData.edges?.length 
                });
                
                if (diagramData.nodes && diagramData.edges) {
                  // Log quais nós têm selectedComponentId
                  const nodesWithComponents = diagramData.nodes.filter((node: Node<NodeData>) => 
                    node.data.selectedComponentId && node.data.selectedComponentId !== 'novo'
                  );
                  console.log('🔗 [useDiagramLoad] Nodes with selectedComponentId:', 
                    nodesWithComponents.map((node: Node<NodeData>) => ({
                      id: node.id,
                      selectedComponentId: node.data.selectedComponentId
                    }))
                  );
                  
                  // Buscar dados das novas colunas para componentes que possuem selectedComponentId
                  const nodesWithCodeChanges = await Promise.all(
                    diagramData.nodes.map(async (node: Node<NodeData>) => {
                      if (node.data.selectedComponentId && node.data.selectedComponentId !== 'novo') {
                        console.log('🔍 [useDiagramLoad] Fetching component data for:', {
                          nodeId: node.id,
                          selectedComponentId: node.data.selectedComponentId
                        });
                        
                        try {
                          // Buscar os dados do componente incluindo nome, métricas e dados do tipo
                          const { data: componentData, error: componentError } = await supabase
                            .from('components')
                            .select(`
                              id, 
                              name,
                              code_changes_7days,
                              code_changes_30days,
                              deploys_7days,
                              id_component_type,
                              component_types (
                                id,
                                name,
                                icon
                              )
                            `)
                            .eq('id', node.data.selectedComponentId)
                            .single();

                          if (componentError || !componentData) {
                            console.error('❌ [useDiagramLoad] Error fetching component:', {
                              componentId: node.data.selectedComponentId,
                              error: componentError
                            });
                            return node;
                          }

                          // Logging dos dados carregados para debug
                          console.log('✅ [useDiagramLoad] Component data loaded:', {
                            id: componentData.id,
                            name: componentData.name,
                            id_component_type: componentData.id_component_type,
                            code_changes_7days: componentData.code_changes_7days,
                            code_changes_30days: componentData.code_changes_30days,
                            deploys_7days: componentData.deploys_7days,
                            component_types: componentData.component_types
                          });

                          // Verificar se dados do tipo foram carregados
                          if (!componentData.component_types) {
                            console.warn('⚠️ [useDiagramLoad] Component type data not loaded for component:', componentData.id);
                          }

                          // Atualizar o nó com todos os dados do componente
                          const updatedNode = {
                            ...node,
                            data: {
                              ...node.data,
                              selectedComponentName: componentData.name,
                              componentTypeName: componentData.component_types?.name || 'Tipo não definido',
                              componentTypeIcon: componentData.component_types?.icon || 'box',
                              codeChanges7Days: componentData.code_changes_7days || 0,
                              codeChanges30Days: componentData.code_changes_30days || 0,
                              deploys7Days: componentData.deploys_7days || 0,
                            }
                          };

                          console.log('🔄 [useDiagramLoad] Updated node data:', {
                            nodeId: updatedNode.id,
                            selectedComponentName: updatedNode.data.selectedComponentName,
                            componentTypeName: updatedNode.data.componentTypeName,
                            componentTypeIcon: updatedNode.data.componentTypeIcon
                          });

                          return updatedNode;
                        } catch (error) {
                          console.error('❌ [useDiagramLoad] Error processing component:', {
                            componentId: node.data.selectedComponentId,
                            error: error
                          });
                          return node;
                        }
                      }
                      return node;
                    })
                  );

                  console.log('🏗️ [useDiagramLoad] Setting nodes and edges');
                  setNodes(nodesWithCodeChanges);
                  setEdges(diagramData.edges);
                  
                  console.log('📚 [useDiagramLoad] Initializing history');
                  // Initialize history with the loaded state - só depois que nodes e edges estão definidos
                  if (setHistory && setHistoryIndex) {
                    setHistory([{ nodes: nodesWithCodeChanges, edges: diagramData.edges }]);
                    setHistoryIndex(0);
                  }
                  
                  console.log('✅ [useDiagramLoad] Diagram fully loaded and history initialized');
                }
              } catch (parseError) {
                console.error("Error parsing diagram data:", parseError);
              }
            }
            
            // Só marcar como carregado DEPOIS de TUDO estar pronto
            console.log('🎯 [useDiagramLoad] Marking diagram as loaded');
            setDiagramLoaded(true);
          }
        } catch (error) {
          console.error("Failed to fetch diagram info:", error);
        } finally {
          setIsLoading(false);
        }
      }
    };
    
    fetchDiagramInfo();
  }, [id]); // Remover setters das dependências para evitar re-execuções

  return {
    diagramName,
    diagramLoaded,
    isLoading
  };
}
